import React, { useState } from 'react'
import { makeStyles, Typography, useTheme } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import {
  getObjectLabels,
  getTieredPackageColor,
  getTieredPackageDescriptionText,
  getTieredPackageFont,
  withAlpha
} from '@paintscout/util/builder'
import type {
  TieredPackageContentType,
  TieredPackageFeature,
  TieredPackageOrientation,
  TieredPackagePricePosition,
  TieredPackageTier
} from 'paintscout'
import classNames from 'classnames'
import { CheckIcon, DollarSignIcon, PackageIcon, PriceDots, XmarkIcon } from './icons'
import { useClientOptions } from '@paintscout/ui-business'
import { column, row } from './tieredPackageStyles'

/**
 * A single package card as the customer sees it — used for the live preview in the template
 * editor. Color, font, icon, orientation and price placement all come from the stored package
 * set; with no accent color set it falls back to the neutral styling from the original design.
 */

const useStyles = makeStyles<Theme>(
  (theme) => {
    const { Neutral } = theme.paintscout.palette.brand
    return {
      root: {
        display: 'flex',
        position: 'relative',
        minWidth: 0,
        cursor: 'pointer'
      },
      vertical: {
        flex: '1 1 0'
      },
      horizontal: {
        width: '100%'
      },
      badge: {
        position: 'absolute',
        top: -11,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1,
        padding: '7px 12px',
        borderRadius: 20,
        background: Neutral.P98,
        border: `1px solid ${Neutral.P85}`,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: '15px',
        color: Neutral.P60,
        whiteSpace: 'nowrap'
      },
      badgePriceFirst: {
        top: 0,
        transform: 'translate(-50%, -50%)'
      },
      // The badge doubles as the control that moves it: click an unbadged card to take it.
      badgeInteractive: {
        cursor: 'pointer',
        transition: 'background-color 0.12s',
        '&:hover': {
          background: Neutral.P96
        }
      },
      /**
       * The offer on a card that has no badge — shown on hover and on keyboard focus, and so
       * invisible on a touch screen. That is deliberate: revealing it there puts a "Recommended"
       * chip on all three cards at once, which reads as three recommendations. Touch moves the
       * badge through the tier actions menu instead, whose "Add recommended badge" row acts on
       * the tier the pills have selected.
       */
      badgeGhost: {
        borderStyle: 'dashed',
        opacity: 0,
        transition: 'opacity 0.12s',
        '&:focus': {
          opacity: 1
        }
      },
      badgeGhostVisible: {
        opacity: 1
      },

      // Panes are borderless; the group draws the borders so adjacent edges collapse.
      stack: {
        display: 'flex',
        flex: 1,
        minWidth: 0,
        '& > *': {
          border: `1px solid ${Neutral.P85}`,
          boxSizing: 'border-box'
        }
      },
      stackVertical: {
        flexDirection: 'column',
        '& > * + *': {
          borderTop: 'none'
        },
        '& > *:first-child': {
          borderRadius: '10px 10px 0 0'
        },
        '& > *:last-child': {
          borderRadius: '0 0 10px 10px'
        }
      },
      stackHorizontal: {
        flexDirection: 'row',
        alignItems: 'stretch',
        '& > * + *': {
          borderLeft: 'none'
        },
        '& > *:first-child': {
          borderRadius: '10px 0 0 10px'
        },
        '& > *:last-child': {
          borderRadius: '0 10px 10px 0'
        }
      },
      // Title-first / price-first tuck the leading pane 16px under the rest of the card
      split: {
        ...column(),
        flex: 1,
        minWidth: 0,
        // A card with few features would otherwise sit shorter than its neighbours.
        minHeight: 285
      },
      splitLead: {
        marginBottom: -16,
        overflow: 'hidden',
        border: `1px solid ${Neutral.P85}`,
        boxSizing: 'border-box',
        borderRadius: '10px 10px 0 0',
        borderBottom: 'none'
      },
      splitRest: {
        position: 'relative',
        zIndex: 1
      },

      header: {
        ...column(8),
        overflow: 'hidden'
      },
      headerVertical: {
        padding: '16px 16px 32px'
      },
      headerHorizontal: {
        width: 200,
        flexShrink: 0,
        padding: 16,
        justifyContent: 'center'
      },
      headerIcon: {
        display: 'flex',
        flexShrink: 0
      },
      headerText: {
        ...column(4),
        padding: '0 2px'
      },
      name: {
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: 0.8,
        lineHeight: 'normal'
      },
      tagline: {
        fontSize: theme.typography.pxToRem(12),
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: '15px'
      },

      price: {
        flexShrink: 0,
        ...row(8),
        padding: 16,
        overflow: 'hidden',
        background: theme.palette.common.white
      },
      pricePaddedForOverlap: {
        padding: '16px 16px 32px'
      },
      priceHorizontal: {
        width: 180
      },
      priceBlock: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
        // `priceSubLineFar`'s `marginLeft: auto` only has somewhere to push the sub-line to if this
        // box spans the whole price row — without `flex: 1` it shrinks to its own content width (the
        // parent `.price` row does not stretch it), so 'difference' mode's total sat bunched right
        // next to the delta instead of parked at the card's far edge.
        flex: 1
      },
      priceText: {
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 4,
        fontSize: theme.typography.pxToRem(20),
        fontWeight: theme.typography.fontWeightMedium
      },
      /**
       * The real total beside a headline delta, so "+$500" still says what the package costs.
       * Takes the title's colour at 60% rather than a fixed grey, so it tints with the tier.
       */
      priceSubLine: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: theme.typography.fontWeightRegular,
        opacity: 0.6,
        whiteSpace: 'nowrap'
      },
      /** 'difference' parks the total at the far end of the row; 'both' keeps the pair together. */
      priceSubLineFar: {
        marginLeft: 'auto'
      },
      /** Each half stays whole; the wrap lands between the total and its delta. */
      priceTextPart: {
        whiteSpace: 'nowrap',
        wordBreak: 'normal'
      },

      body: {
        flex: 1,
        minWidth: 0,
        ...column(10),
        padding: '0 16px',
        background: theme.palette.common.white
      },
      bodyVertical: {
        paddingTop: 16,
        paddingBottom: 28
      },
      bodyHorizontal: {
        paddingTop: 16,
        paddingBottom: 16,
        justifyContent: 'center'
      },
      feature: {
        ...row(8),
        fontSize: theme.typography.pxToRem(12),
        color: Neutral.P60
      },
      featureExcluded: {
        opacity: 0.5
      },
      description: {
        fontSize: theme.typography.pxToRem(12),
        lineHeight: 1.45,
        color: Neutral.P60,
        // Descriptions are plain text now, so newlines need to survive.
        whiteSpace: 'pre-wrap',
        '& p': {
          margin: 0
        }
      },

      footer: {
        flexShrink: 0,
        background: theme.palette.common.white,
        padding: 12
      },
      footerTight: {
        padding: 8
      },
      footerHorizontal: {
        width: 160,
        display: 'flex',
        alignItems: 'center'
      },
      button: {
        height: 32,
        ...row(),
        justifyContent: 'center',
        padding: '0 10px',
        boxSizing: 'border-box',
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightMedium,
        whiteSpace: 'nowrap'
      },
      buttonFullWidth: {
        width: '100%'
      }
    }
  },
  { name: 'TieredPackagePreviewCard' }
)

/**
 * Colors for a card with no accent chosen. The design's greys are our brand neutrals; its
 * #354962 title has no token, so it maps to the nearest one (P40, #394860).
 */
function neutralColors({ Neutral }: Theme['paintscout']['palette']['brand']) {
  return {
    header: Neutral.P98,
    headerRecommended: `linear-gradient(180deg, ${withAlpha(Neutral.P75, 0.15)} 0%, ${withAlpha(Neutral.P40, 0.15)} 100%)`,
    title: Neutral.P40,
    tagline: Neutral.P60,
    buttonBg: Neutral.P98,
    buttonBgRecommended: Neutral.P94,
    buttonText: Neutral.P60,
    buttonTextRecommended: Neutral.P40,
    iconPlaceholder: Neutral.P85
  }
}

export interface TieredPackagePreviewCardProps {
  tier: TieredPackageTier
  /** Positional label used when the tier has no customer-facing name. */
  fallbackName: string
  /** The tier's own rows, in order. An absent `included` reads as included. */
  features: TieredPackageFeature[]
  contentType: TieredPackageContentType
  recommended?: boolean
  badgeLabel?: string
  /**
   * Accent colour for the card. Belongs to the package set; falls back to the tier's own legacy
   * value so a template saved by an older client still renders in colour.
   */
  color?: string
  /**
   * Moves the recommended badge onto this card, or clears it when this card already has it.
   * Omitted on read-only previews, which then show the badge without offering to move it.
   */
  onBadgeClick?: () => void
  font?: string
  orientation?: TieredPackageOrientation
  pricePosition?: TieredPackagePricePosition
  /**
   * Rendered instead of the masked dots when the caller knows the price, one entry per part
   * that may wrap onto its own line (see formatTierPriceParts).
   */
  priceParts?: string[]
  /**
   * A second, smaller figure beside `priceParts` — the total next to a headline delta. See
   * formatTierPriceLines.
   */
  priceSubLine?: string
  /** Keeps `priceSubLine` next to the main figure instead of at the far end of the row. */
  priceSubInline?: boolean
  /**
   * The button's label. Defaults to "View <estimate>", which is what the settings and drawer previews
   * show. The customer-facing surfaces name the package instead — "View Premium", "Switch to
   * Premium", "Selected" — so they pass their own.
   */
  ctaText?: string
  onClick?: () => void
}

export default function TieredPackagePreviewCard(props: TieredPackagePreviewCardProps) {
  const {
    tier,
    fallbackName,
    features,
    contentType = 'features',
    recommended,
    badgeLabel,
    color,
    onBadgeClick,
    font,
    orientation = 'vertical',
    pricePosition = 'title-first',
    priceParts,
    priceSubLine,
    priceSubInline,
    ctaText,
    onClick
  } = props
  const classes = useStyles()
  // Only drives the dashed "Move the badge here" offer, so it is not tracked on read-only previews.
  const [hovered, setHovered] = useState(false)
  const theme = useTheme()
  const palette = neutralColors(theme.paintscout.palette.brand)
  const { options } = useClientOptions()
  const { quote: quoteLabel } = getObjectLabels({ options })
  const isHorizontal = orientation === 'horizontal'
  // Price position applies to both orientations: it swaps the leading pane on vertical cards
  // and the first column on horizontal ones.
  const leadsWithPrice = pricePosition === 'price-first'
  const titleFirst = !isHorizontal && !leadsWithPrice
  const priceFirst = !isHorizontal && leadsWithPrice

  const descriptionText = getTieredPackageDescriptionText(tier.description)
  const accent = color ?? tier.color
  const neutral = !accent
  const ramp = getTieredPackageColor(accent)
  const { family, cardSize } = getTieredPackageFont(font)
  const emphasized = !!recommended

  const headerBackground = neutral
    ? emphasized
      ? palette.headerRecommended
      : palette.header
    : emphasized
      ? `linear-gradient(180deg, ${withAlpha(ramp.vivid1, 0.15)} 0%, ${withAlpha(ramp.vivid2, 0.15)} 100%)`
      : withAlpha(ramp.base, 0.05)
  const titleColor = neutral ? palette.title : ramp.dark
  const taglineColor = neutral ? palette.tagline : withAlpha(ramp.dark, 0.6)
  const buttonBackground = neutral
    ? emphasized
      ? palette.buttonBgRecommended
      : palette.buttonBg
    : emphasized
      ? ramp.dark
      : withAlpha(ramp.dark, 0.15)
  const buttonColor = neutral
    ? emphasized
      ? palette.buttonTextRecommended
      : palette.buttonText
    : emphasized
      ? theme.palette.common.white
      : ramp.dark
  const buttonRadius = neutral ? (emphasized ? 8 : 6) : 8

  const rows = features.filter((feature) => feature.label.trim())

  const headerPane = (
    <div
      key="header"
      className={classNames(classes.header, {
        [classes.headerHorizontal]: isHorizontal,
        [classes.headerVertical]: !isHorizontal
      })}
      style={{ background: headerBackground }}
    >
      <div className={classes.headerIcon}>
        <PackageIcon
          icon={tier.icon}
          size={24}
          color={titleColor}
          placeholderColor={neutral ? palette.iconPlaceholder : withAlpha(ramp.dark, 0.35)}
        />
      </div>
      <div className={classes.headerText}>
        <Typography className={classes.name} style={{ fontFamily: family, fontSize: cardSize, color: titleColor }}>
          {tier.name || fallbackName}
        </Typography>
        {!!tier.tagline && (
          <Typography className={classes.tagline} style={{ color: taglineColor }}>
            {tier.tagline}
          </Typography>
        )}
      </div>
    </div>
  )

  const pricePane = (
    <div
      key="price"
      className={classNames(classes.price, {
        [classes.pricePaddedForOverlap]: priceFirst,
        [classes.priceHorizontal]: isHorizontal
      })}
    >
      {priceParts?.length ? (
        <div className={classes.priceBlock}>
          <Typography className={classes.priceText} style={{ color: titleColor }}>
            {priceParts.map((part) => (
              <span key={part} className={classes.priceTextPart}>
                {part}
              </span>
            ))}
          </Typography>
          {!!priceSubLine && (
            <Typography
              className={classNames(classes.priceSubLine, { [classes.priceSubLineFar]: !priceSubInline })}
              style={{ color: titleColor }}
            >
              {priceSubLine}
            </Typography>
          )}
        </div>
      ) : (
        <>
          <DollarSignIcon size={20} />
          <PriceDots />
        </>
      )}
    </div>
  )

  const bodyPane = (
    <div
      key="body"
      className={classNames(classes.body, {
        [classes.bodyHorizontal]: isHorizontal,
        [classes.bodyVertical]: !isHorizontal
      })}
    >
      {contentType !== 'features' && descriptionText && <div className={classes.description}>{descriptionText}</div>}
      {contentType !== 'description' &&
        rows.map((feature) => (
          <span
            key={feature.id}
            className={classNames(classes.feature, { [classes.featureExcluded]: feature.included === false })}
          >
            {feature.included === false ? <XmarkIcon /> : <CheckIcon />}
            {feature.label}
          </span>
        ))}
    </div>
  )

  const footerPane = (
    <div
      key="footer"
      className={classNames(classes.footer, {
        [classes.footerTight]: titleFirst || priceFirst || isHorizontal,
        [classes.footerHorizontal]: isHorizontal
      })}
    >
      <div
        className={classNames(classes.button, { [classes.buttonFullWidth]: isHorizontal })}
        style={{ background: buttonBackground, color: buttonColor, borderRadius: buttonRadius }}
      >
        {ctaText ?? `View ${quoteLabel.value}`}
      </div>
    </div>
  )

  const leadPane = priceFirst ? pricePane : headerPane
  const restPanes = priceFirst ? [headerPane, bodyPane, footerPane] : [pricePane, bodyPane, footerPane]

  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames(classes.root, {
        [classes.horizontal]: isHorizontal,
        [classes.vertical]: !isHorizontal
      })}
      onClick={onClick}
      onMouseEnter={onBadgeClick ? () => setHovered(true) : undefined}
      onMouseLeave={onBadgeClick ? () => setHovered(false) : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
    >
      {(recommended || !!onBadgeClick) && (
        <span
          {...(onBadgeClick && {
            role: 'button',
            tabIndex: 0,
            title: recommended ? 'Remove the badge' : 'Move the badge here',
            onClick: (e: React.MouseEvent) => {
              // The card itself selects the tier, so the badge must not do both.
              e.stopPropagation()
              onBadgeClick()
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                onBadgeClick()
              }
            }
          })}
          className={classNames(classes.badge, {
            [classes.badgePriceFirst]: priceFirst,
            [classes.badgeInteractive]: !!onBadgeClick,
            [classes.badgeGhost]: !recommended,
            [classes.badgeGhostVisible]: !recommended && hovered
          })}
        >
          {badgeLabel?.trim() || 'Recommended'}
        </span>
      )}
      {isHorizontal ? (
        <div className={classNames(classes.stack, classes.stackHorizontal)}>
          {leadsWithPrice
            ? [pricePane, headerPane, bodyPane, footerPane]
            : [headerPane, pricePane, bodyPane, footerPane]}
        </div>
      ) : (
        <div className={classes.split}>
          <div className={classes.splitLead}>{leadPane}</div>
          <div className={classNames(classes.stack, classes.stackVertical, classes.splitRest)}>{restPanes}</div>
        </div>
      )}
    </div>
  )
}
